import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * Put a phone into the international form the app stores, or leave it exactly as given.
 *
 * The one rule for phone numbers arriving from OUTSIDE the app — a spreadsheet, a paste, an import —
 * where nothing guarantees the shape that `PhoneNumberInput` produces. Both bulk importers need it and
 * had grown their own copy; this is the shared one, so they can't drift.
 *
 * Spreadsheets damage phone numbers on the way in. Google Sheets reads a leading "+" as a formula and a
 * leading "0" as an insignificant digit, so "+61412345678" and "0412345678" both arrive stripped, and
 * the quote mark that prevents it is easy to forget. Given the operator's own country, libphonenumber
 * puts back what was lost: "412345678", "61412345678" and "0412345678" all resolve to +61412345678.
 *
 * It refuses to guess, which is the important part. A foreign number that lost its "+" —
 * "639228127560" for a Philippine mobile — is not a valid Australian number in any reading, so it comes
 * back unchanged rather than being bent into one. Better a row somebody has to fix than a driver
 * ringing a number that belongs to a stranger.
 *
 * The country to pass is the operator's: `defaultCountrySelector`, which is what the manual booking
 * form's PhoneNumberInput renders with, so a number typed in a spreadsheet and a number typed in the
 * booking form are read the same way.
 */
export const normalisePhone = (value: string, country?: CountryCode): string => {
  if (!value) return value;

  const parsed = parsePhoneNumberFromString(value, country);

  return parsed?.isValid() ? parsed.number : value;
};
